import { SelectionLocation } from "@/board/location/SelectionLocation";
import { isBacklogBoard, isObjectivesBoard, isStickyBoard } from "@/model/board";
import { useActivityStore } from "@/store/activity";
import { useArtObjectivesPanelStore } from "@/store/artObjectivesPanel";
import { useBoardStore } from "@/store/board";
import { useBoardConfigStore } from "@/store/boardConfig";
import { useBoardMenuStore } from "@/store/boardMenu";
import { useClientSettingsStore } from "@/store/clientSettings";
import { useDrawStore } from "@/store/draw";
import { useIssueSelectorStore } from "@/store/issueSelector";
import { useMessengerStore } from "@/store/messenger";
import { usePanModeStore } from "@/store/panMode";
import { usePointerStore } from "@/store/pointer";
import { useSearchMenuStore } from "@/store/search/searchMenu";
import { useSightlineStore } from "@/store/sightline";
import { useSwitchSessionStore } from "@/store/switchSession";
import { useTimerStore } from "@/store/timer";
import { useUserStore } from "@/store/user";
import { useWorkModeStore } from "@/store/workMode";

import { action, ctxAction, defineActions, untrackedAction } from "./actions";
import { boardActions } from "./boardActions";
import { navigationActions } from "./navigationActions";
import type { ActionContext } from "./types";

export const toggleActions = defineActions("toggle", {
  toggleStickyMagnify: action((magnify?: boolean) => useBoardStore().toggleStickyMagnify(magnify), {
    name: /*$t*/ "action.stickyMagnify",
    icon: "general/sticky-magnify",
    autoToggle: true,
    active: () => useBoardStore().magnifying,
    disabled: () =>
      isBacklogBoard(useBoardStore().board?.type) ||
      useBoardStore().creatableStickyTypes.length === 0 ||
      useDrawStore().active,
  }),

  toggleTrailingPointerMode: action(() => usePointerStore().toggle(), {
    name: /*$t*/ "action.pointer",
    icon: "general/pointer",
    active: () => usePointerStore().active,
    disabled: () => usePointerStore().isDisabled,
  }),
  toggleTrailingPointer: action((active?: boolean) => usePointerStore().toggle(active), {
    name: /*$t*/ "action.pointer",
    icon: "general/pointer",
    autoToggle: true,
    active: () => usePointerStore().active,
    disabled: () => usePointerStore().isDisabled,
  }),

  togglePanMode: action(() => usePanModeStore().toggle(), {
    name: /*$t*/ "action.panMode",
    icon: "general/pan",
    active: () => usePanModeStore().active,
    disabled: () => useBoardStore().creatableStickyTypes.length === 0,
  }),
  togglePan: action((active?: boolean) => usePanModeStore().toggle(active), {
    name: /*$t*/ "action.panMode",
    icon: "general/pan",
    autoToggle: true,
    active: () => usePanModeStore().active,
    disabled: () => useBoardStore().creatableStickyTypes.length === 0,
  }),
  showPanMode: action(() => {
    if (usePanModeStore().toggle(true)) {
      boardActions.clearCardSelection("internal");
    }
  }),
  hidePanMode: action(() => {
    usePanModeStore().toggle(false);
    boardActions.clearCardSelection("internal");
  }),

  showConfidenceVoteModal: untrackedAction(
    (ctx, attrs?) => navigationActions.openModal(ctx, "ConfidenceVoteDashboardModal", { attrs }),
    { name: /*$t*/ "action.confidenceVote", icon: "confidence/vote" },
  ),
  toggleWorkMode: ctxAction(
    (ctx) => {
      useWorkModeStore().toggle();
      ctx.track({ workMode: useWorkModeStore().workingMode });
    },
    { name: /*$t*/ "action.toggleWorkMode" },
  ),
  showTimer: action(toggleTimer, { name: /*$t*/ "action.timer", icon: "general/timer" }),
  toggleBoardHistory: ctxAction(
    (ctx) => {
      ctx.track({ open: !useActivityStore().isBoardActivityOpen });
      return useActivityStore().toggleBoard();
    },
    { name: /*$t*/ "boardDiff.boardTitle", icon: "general/board-history" },
  ),
  toggleArtObjectivesPanel: ctxAction(
    (ctx) => {
      ctx.track({ open: !useArtObjectivesPanelStore().active });
      useArtObjectivesPanelStore().toggle();
    },
    {
      name: /*$t*/ "artObjectivesPanel.title",
      icon: "board/objectives-art",
      active: () => useArtObjectivesPanelStore().active,
    },
  ),
  toggleSightline: ctxAction(
    (ctx) => {
      ctx.track({ open: !useSightlineStore().isOpen });
      useSightlineStore().toggle();
    },
    {
      name: /*$t*/ "sightline.title",
      icon: "general/sightline",
      active: () => useSightlineStore().isOpen,
    },
  ),
  toggleCardHistory: ctxAction(
    (ctx) => {
      const cardIds = useBoardStore().selectedStickyIds;
      if (cardIds.length !== 1 && !useActivityStore().card) {
        return ctx.track(null);
      }
      ctx.track({ open: !useActivityStore().isCardActivityOpen });
      return useActivityStore().toggleCard(cardIds[0]);
    },
    {
      name: /*$t*/ "cardAction.history",
      label: /*$t*/ "label.actionMenu.history",
      icon: "general/board-history",
    },
  ),
  showSwitchSession: ctxAction(
    (ctx) => {
      if (useSwitchSessionStore().active) {
        ctx.track(null);
      }
      useSwitchSessionStore().toggle();
    },
    { name: /*$t*/ "switchSession.title", active: () => useSwitchSessionStore().active },
  ),
  toggleSearch: ctxAction(toggleSearch, {
    name: /*$t*/ "action.search",
    icon: "general/search",
    disabled: () => !isStickyBoard(useBoardStore().board?.type),
  }),
  showTeamObjectives: untrackedAction(showTeamObjectives, { name: /*$t*/ "action.teamObjectives" }),
  showOverview: untrackedAction(
    (ctx, attrs?) => navigationActions.openModal(ctx, "OverviewModal", { attrs }),
    { name: /*$t*/ "action.showOverview" },
  ),
  showSelectionOverview: untrackedAction(
    (ctx) =>
      navigationActions.openModal(ctx, "OverviewModal", {
        attrs: {
          boardId: useBoardStore().currentBoard().id,
          location: new SelectionLocation(useBoardStore().selectedStickyIds).id(),
          load: useBoardStore().selectedStickies.reduce((sum, card) => sum + card.data.points, 0),
        },
      }),
    { name: /*$t*/ "action.showOverview", icon: "general/search-results" },
  ),
  showProgramMetrics: untrackedAction(
    (ctx) => navigationActions.openModal(ctx, "ArtMetricsModal"),
    {
      name: /*$t*/ "action.programMetrics",
      icon: "board/metrics",
      disabled: () => useBoardStore().board?.type !== "program",
    },
  ),
  showTeamMetrics: untrackedAction((ctx) => navigationActions.openModal(ctx, "TeamMetricsModal"), {
    name: /*$t*/ "action.teamMetrics",
    icon: "board/metrics",
    disabled: () => useBoardStore().board?.type !== "team",
  }),
  shiftMenu: action(() => useBoardMenuStore().toggleMenuPosition(), {
    name: /*$t*/ "action.shiftMenu",
    icon: "arrow/shift-right",
  }),
  showStats: action(() => useClientSettingsStore().toggleShowStats(1)),
  showStatGraphs: action(() => useClientSettingsStore().toggleShowStats(2)),
  showDraw: action(() => useDrawStore().toggle(), {
    name: /*$t*/ "contextMenu.drawModeOn",
    icon: "general/draw-lines",
  }),
  hideDraw: action(() => useDrawStore().toggle(), {
    name: /*$t*/ "contextMenu.drawModeOff",
    icon: "general/draw-lines",
  }),
  showSettings: untrackedAction((ctx) => navigationActions.openModal(ctx, "SettingsModal"), {
    name: /*$t*/ "userAction.settings",
    label: /*$t*/ "label.mainMenu.settings",
    icon: "system/settings",
  }),
  showShortcuts: untrackedAction((ctx) => navigationActions.openModal(ctx, "ShortcutsModal"), {
    name: /*$t*/ "userAction.keyboardShortcuts",
    label: /*$t*/ "label.mainMenu.keyboardShortcuts",
    icon: "general/shortcuts",
  }),
  toggleArchivedSessions: action(() => useClientSettingsStore().toggleArchivedSessions(), {
    name: /*$t*/ "sessionSelectionModal.showArchived",
  }),
  showIntercom: action(() => window.Intercom("showSpace", "help"), {
    name: /*$t*/ "action.messenger",
    icon: "general/help",
    disabled: () => !useMessengerStore().show || !window.Intercom,
  }),
  toggleIssueSelector: action(() => useIssueSelectorStore().toggle(), {
    name: /*$t*/ "issueSelector.panelTitle",
    icon: "general/work-item-selector",
    active: () => useIssueSelectorStore().active,
  }),
  showBoardConfig: action(() => useBoardConfigStore().toggle(), {
    name: /*$t*/ "action.boardConfig",
    icon: "system/settings",
    active: () => useBoardConfigStore().active,
    disabled: () =>
      !useUserStore().isAllowed("cockpit") || isObjectivesBoard(useBoardStore().board?.type),
  }),
});

function toggleTimer() {
  useTimerStore().toggle();
  useBoardMenuStore().setMenuPosition(useTimerStore().active ? "left" : "default");
}

function toggleSearch(ctx: ActionContext, keepFilters?: boolean) {
  const isActive = useSearchMenuStore().isSearchOpen;
  // help event code analyzer
  if (isActive) {
    ctx.track({ open: false, keepFilters: !!keepFilters });
  } else {
    ctx.track({ open: true });
  }
  if (isActive && !keepFilters) {
    useSearchMenuStore().resetCriteria();
  }
  useSearchMenuStore().toggleSearch();
}

function showTeamObjectives(ctx: ActionContext) {
  const board = useBoardStore().currentBoard();
  if (board.type === "team") {
    navigationActions.openModal(ctx, "TeamObjectivesModal", { attrs: { boardId: board.id } });
  }
}
